import { useFormApi, useFormState } from 'informed';
import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { Checkbox, FormGroup, RichText, Select } from 'components/common/form';
import { Alert } from 'components/common';

interface Props {
  itemId?: number;
  itemAnswerableByUserLabel: string;
  itemLabel: string;
  itemMarkableByUserLabel: string;
  numberOfItems: number;
}

const AchievementTemplateItemForm = ({
  itemId,
  itemAnswerableByUserLabel,
  itemLabel,
  itemMarkableByUserLabel,
  numberOfItems
}: Props) => {
  const {
    messages,
    pageState: { achievement }
  } = useReduxState();

  const formApi = useFormApi();

  const formState = useFormState();

  const handleToggleAnswerable = (checked: boolean) => {
    if (checked || achievement.type === 'conversation') return;
    formApi.setValue('isMarkableByUser', false);
  };

  const validateItem = (value: string) => {
    return !value?.trim() ? messages.commonRequired : undefined;
  };

  const shouldUserSeeIncomplete =
    !achievement.isIncompleteViewableByUser &&
    (achievement.type === 'tasklist'
      ? formState.values.isMarkableByUser
      : formState.values.isAnswerableByUser);

  return (
    <div className="item-form">
      <FormGroup label={itemLabel} maxWidth="100%">
        <RichText
          autoGrow={true}
          id={`text-${itemId || 0}`}
          field="text"
          rows={3}
          validate={validateItem}
          validateOnChange={true}
        />
      </FormGroup>

      <FormGroup label={messages.commonPosition}>
        <Select
          id={`order-${itemId || 0}`}
          field="order"
          options={Array.from(
            Array(itemId ? numberOfItems : numberOfItems + 1),
            (_, i) => ({
              id: i,
              label: i + 1,
              value: i + 1
            })
          )}
          style={{ maxWidth: 100 }}
        />
      </FormGroup>

      <FormGroup label={messages.commonEditingRights} style={{ marginTop: 25 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {achievement.type !== 'tasklist' ? (
            <Checkbox
              id={`is-answerable-by-user-${itemId || 0}`}
              field="isAnswerableByUser"
              label={itemAnswerableByUserLabel}
              onChange={(ev) =>
                handleToggleAnswerable(ev.currentTarget.checked)
              }
            />
          ) : null}

          {achievement.type !== 'conversation' ? (
            <Checkbox
              disabled={
                achievement.type === 'group-conversation' &&
                !formState.values.isAnswerableByUser
              }
              id={`is-markable-by-user-${itemId || 0}`}
              field="isMarkableByUser"
              label={itemMarkableByUserLabel}
              {...(achievement.type === 'group-conversation'
                ? {
                    labelHelpText:
                      messages.achievementItemsQuestionMarkableByUserHelpText
                  }
                : {})}
            />
          ) : null}
        </div>
      </FormGroup>

      {shouldUserSeeIncomplete ? (
        <Alert
          style={{ marginTop: 30 }}
          text={getMessageWithPlaceholders({
            message:
              achievement.type === 'tasklist'
                ? messages.achievementIncompleteToggleInfoTasklist
                : messages.achievementIncompleteToggleInfo,
            placeholders: {
              section: messages.eventCalendarSettingsAccess.toLowerCase()
            }
          })}
          type="warning-light"
        />
      ) : null}
    </div>
  );
};

export default AchievementTemplateItemForm;
